import dayjs from 'dayjs'
import { isNil } from 'lodash-es'
import { AdGroupStatus, BudgetCapping, CampaignQuery, Status } from '../../../__generated__/graphql'
import { ADGROUP_END_DATE_THRESHOLD_DAYS } from '../../../env'

export type CampaignBudgetAdGroup = NonNullable<CampaignQuery['campaign']['adGroups']>[number]

export type CampaignLeftoverCaption =
  | { type: 'leftBeforeDate'; leftBudget: number; endDate: string }
  | { type: 'leftThisMonth'; leftBudget: number }
  | { type: 'left'; leftBudget: number }
  | null

export type CampaignLeftoverWarning = 'endDateSoon' | 'endOfMonthSoon' | null

export type CampaignTooltipRow =
  | { type: 'monthly'; name: string; leftBudget: number }
  | { type: 'period'; name: string; leftBudget: number }
  | { type: 'overall'; name: string; leftBudget: number }
  | { type: 'open'; name: string }

const isCapped = (adGroup: CampaignBudgetAdGroup): boolean => (adGroup.budgetCapping ?? 0) > 0

export const getActiveAdGroups = (adGroups: CampaignBudgetAdGroup[] | undefined | null): CampaignBudgetAdGroup[] =>
  adGroups?.filter((adGroup) => adGroup.status === AdGroupStatus.Active) ?? []

export const getAdGroupLeftoverAmount = (adGroup: CampaignBudgetAdGroup): number | null => {
  if (!isCapped(adGroup)) return null

  const measures = adGroup.budget?.budgetMeasures
  if (!measures) return null

  switch (adGroup.budgetCappingType) {
    case BudgetCapping.Monthly:
      if (isNil(measures.monthBudget)) return null
      return measures.monthBudget - measures.monthSpent
    case BudgetCapping.Period:
      if (isNil(measures.periodBudget)) return null
      return measures.periodBudget - measures.periodSpent
    case BudgetCapping.Overall:
      if (isNil(measures.overallBudget)) return null
      return measures.overallBudget - measures.overallSpent
    default:
      return null
  }
}

export const sumActiveAdGroupsLeftover = (activeAdGroups: CampaignBudgetAdGroup[]): number | null => {
  let total = 0
  let hasValue = false

  activeAdGroups.forEach((adGroup) => {
    const leftover = getAdGroupLeftoverAmount(adGroup)
    if (!isNil(leftover)) {
      total += leftover
      hasValue = true
    }
  })

  return hasValue ? total : null
}

const allActiveAdGroupsShareEndDate = (activeAdGroups: CampaignBudgetAdGroup[]): string | null => {
  if (activeAdGroups.length === 0) return null

  const endDates = activeAdGroups.map((adGroup) => adGroup.endDate).filter(Boolean) as string[]
  if (endDates.length !== activeAdGroups.length) return null

  const firstDay = dayjs(endDates[0]).format('YYYY-MM-DD')
  return endDates.every((endDate) => dayjs(endDate).format('YYYY-MM-DD') === firstDay) ? endDates[0] : null
}

const allActiveAdGroupsAreMonthly = (activeAdGroups: CampaignBudgetAdGroup[]): boolean =>
  activeAdGroups.length > 0 &&
  activeAdGroups.every((adGroup) => adGroup.budgetCappingType === BudgetCapping.Monthly && isCapped(adGroup))

export const getCampaignLeftoverCaption = (
  campaignStatus: Status,
  adGroups: CampaignBudgetAdGroup[] | undefined | null
): CampaignLeftoverCaption => {
  if (campaignStatus === Status.Archived) return null

  const activeAdGroups = getActiveAdGroups(adGroups)
  if (activeAdGroups.length === 0) return null

  const leftBudget = sumActiveAdGroupsLeftover(activeAdGroups)
  if (isNil(leftBudget)) return null

  const sharedEndDate = allActiveAdGroupsShareEndDate(activeAdGroups)
  if (sharedEndDate) return { type: 'leftBeforeDate', leftBudget, endDate: sharedEndDate }

  if (allActiveAdGroupsAreMonthly(activeAdGroups)) return { type: 'leftThisMonth', leftBudget }

  return { type: 'left', leftBudget }
}

export const isDateWithinThreshold = (date: string | dayjs.Dayjs): boolean =>
  dayjs(date).isBefore(dayjs().add(ADGROUP_END_DATE_THRESHOLD_DAYS, 'day'))

export const getCampaignLeftoverWarning = (
  caption: CampaignLeftoverCaption,
  activeAdGroups: CampaignBudgetAdGroup[]
): CampaignLeftoverWarning => {
  if (!caption) return null

  switch (caption.type) {
    case 'leftBeforeDate':
      return isDateWithinThreshold(caption.endDate) ? 'endDateSoon' : null
    case 'leftThisMonth':
      return isDateWithinThreshold(dayjs().endOf('month')) ? 'endOfMonthSoon' : null
    case 'left':
      return activeAdGroups.some((adGroup) => adGroup.endDate && isDateWithinThreshold(adGroup.endDate))
        ? 'endDateSoon'
        : null
  }
}

export const getCampaignTooltipRows = (activeAdGroups: CampaignBudgetAdGroup[]): CampaignTooltipRow[] =>
  activeAdGroups.map((adGroup) => {
    if (!isCapped(adGroup) || adGroup.budgetCappingType === BudgetCapping.Open) {
      return { type: 'open', name: adGroup.name }
    }

    const leftover = getAdGroupLeftoverAmount(adGroup)
    if (isNil(leftover)) return { type: 'open', name: adGroup.name }

    switch (adGroup.budgetCappingType) {
      case BudgetCapping.Monthly:
        return { type: 'monthly', name: adGroup.name, leftBudget: leftover }
      case BudgetCapping.Period:
        return { type: 'period', name: adGroup.name, leftBudget: leftover }
      case BudgetCapping.Overall:
        return { type: 'overall', name: adGroup.name, leftBudget: leftover }
      default:
        return { type: 'open', name: adGroup.name }
    }
  })
